import React from "react";
import styles from "./FHIRPatient.module.scss";
import PatientName from "./PatientName";
import Identifier from "./Identifier";
import Address from "./Address";
import Gender from "./Gender";
import BirthDate from "./BirthDate";
import Telecom from "./Telecom";
import Deceased from "./Deceased";
import MultipleBirth from "./MultipleBirth";
import MaritalStatus from "./MaritalStatus";
import LastUpdated from "./LastUpdated";
import Coverage from "./Coverage";
import Race from "./Race";
import Ethnicity from "./Ethinicity";
import Disability from "./Disability";
import Religion from "./Religion";
import OrganDonor from "./OrganDonor";
import Language from "./Language";
import AlternateContact from "./AlternateContact";
import LivingArrangement from "./LivingArrangement";

const emptyPatient = {
  identifier: [],
  name: [],
  telecom: [],
  gender: "",
  birthDate: "",
  address: [],
  meta: {
    lastUpdated: ""
  }
};

const FHIRPatientRecord = ({
  patient,
  user,
  summary: isSummary = false,
  showLastUpdated = false,
  ...rest
}) => {
  const _patient = Object.assign({}, emptyPatient, patient);

  const _patientCoverages = _patient.identifier.filter(
    i =>
      "type" in i &&
      "coding" in i.type &&
      i.type.coding.some(c => c.code === "COV")
  );

  const _patientIdentifiers = _patient.identifier.filter(
    i =>
      !(
        "type" in i &&
        "coding" in i.type &&
        i.type.coding.length > 0 &&
        i.type.coding[0].code === "COV"
      )
  );

  const _patientExtensions = _patient.extension ? _patient.extension : "";

  /* const _patientIdentifiers = _patient.identifier.filter(i => !("type" in i)); */

  const name = <PatientName data={_patient.name} summary={isSummary} />;

  const address = (
    <Address
      className={styles.address}
      data={_patient.address}
      summary={isSummary}
    />
  );
  const gender = (
    <Gender
      data={_patient.gender}
      summary={isSummary}
      extensions={_patientExtensions}
    />
  );
  const dob = (
    <BirthDate
      data={_patient.birthDate}
      summary={isSummary}
      extensions={_patientExtensions}
    />
  );
  const telecom = (
    <Telecom
      data={_patient.telecom}
      summary={isSummary}
      extensions={_patientExtensions}
    />
  );
  const identifiers = (
    <Identifier data={_patientIdentifiers} summary={isSummary} />
  );
  const deceased = _patient.hasOwnProperty("deceasedDateTime") ? (
    <Deceased data={_patient.deceasedDateTime} />
  ) : _patient.hasOwnProperty("deceasedBoolean") ? (
    <Deceased data={_patient.deceasedBoolean} />
  ) : null;

  const multipleBirth = _patient.hasOwnProperty("multipleBirthInteger") ? (
    <MultipleBirth data={_patient.multipleBirthInteger} />
  ) : _patient.hasOwnProperty("multipleBirthBoolean") &&
    _patient.multipleBirthBoolean ? (
    <MultipleBirth data={_patient.multipleBirthBoolean} />
  ) : null;

  const maritalStatus = _patient.hasOwnProperty("maritalStatus") ? (
    <MaritalStatus data={_patient.maritalStatus} summary={isSummary} />
  ) : null;

  const language = _patient.hasOwnProperty("communication") ? (
    <Language data={_patient.communication} />
  ) : null;

  const alternateContacts = _patient.hasOwnProperty("contact") ? (
    <AlternateContact data={_patient.contact} />
  ) : null;

  let race,
    ethnicity,
    birthplace,
    disability,
    religion,
    organDonor,
    livingArrangement = null;

  if (_patient.hasOwnProperty("extension")) {
    const birthplaceAddress = _patient.extension.filter(({ url }) =>
      url.includes("patient-birthPlace")
    );
    const raceData = _patient.extension.filter(({ url }) =>
      url.includes("us-core-race")
    );
    const ethnicityData = _patient.extension.filter(({ url }) =>
      url.includes("us-core-ethnicity")
    );
    const disabilityData = _patient.extension.filter(({ url }) =>
      url.includes("patient-disability")
    );
    const religionData = _patient.extension.filter(({ url }) =>
      url.includes("patient-religion")
    );
    const donorData = _patient.extension.filter(({ url }) =>
      url.includes("cadavericDonor")
    );
    const livingArrangementData = _patient.extension.filter(({ url }) =>
      url.includes("LivingArrangement")
    );

    race = raceData.length ? <Race data={raceData} /> : null;

    ethnicity = ethnicityData.length ? (
      <Ethnicity data={ethnicityData} />
    ) : null;

    disability = disabilityData.length ? (
      <Disability data={disabilityData} />
    ) : null;

    religion = religionData.length ? <Religion data={religionData} /> : null;

    organDonor = donorData.length ? <OrganDonor data={donorData} /> : null;

    livingArrangement = livingArrangementData.length ? (
      <LivingArrangement data={livingArrangementData} />
    ) : null;

    birthplace = birthplaceAddress.length ? (
      <Address
        className={styles.address}
        type="birthplaceAddress"
        data={[birthplaceAddress[0].valueAddress]}
      />
    ) : null;
  }

  const coverage =
    _patientCoverages.length > 0 ? (
      <Coverage data={_patientCoverages} summary={isSummary} />
    ) : null;

  const lastUpdated = (
    <LastUpdated
      data={_patient.meta.lastUpdated}
      user={user}
      showLastUpdated={showLastUpdated}
    />
  );

  if (isSummary) {
    return (
      <div {...rest}>
        {name}
        {gender}
        {dob}
        {address}
        {coverage}
        {lastUpdated}
      </div>
    );
  }

  return (
    <div className={styles.base}>
      {name}
      {address}
      {birthplace}
      <section>
        <h3>Basic Demographics</h3>
        <div>
          {gender}
          {dob}
          {deceased}
        </div>
        <div>
          {maritalStatus}
          {multipleBirth}
          {livingArrangement}
        </div>
      </section>
      {telecom}
      {alternateContacts}
      {identifiers}
      {coverage}

      {(_patient.hasOwnProperty("extension") ||
        _patient.hasOwnProperty("communication")) && (
        <section>
          <h3>Additional Information</h3>
          <div>
            {race}
            {ethnicity}
            {disability}
          </div>
          <div>
            {religion}
            {organDonor}
            {language}
          </div>
        </section>
      )}
    </div>
  );
};

export default FHIRPatientRecord;
